import React, { useState, useEffect, useRef } from 'react';
import { X, LogIn, UserPlus, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { trackEvent, trackGeneralEvent } from '../lib/analytics';
import { useTranslation } from 'react-i18next';

interface AuthModalProps {
  onClose: () => void;
  initialIsSignUp?: boolean;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, initialIsSignUp = false }) => {
  const { t } = useTranslation();
  const [isSignUp, setIsSignUp] = useState(initialIsSignUp);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent('auth_modal_viewed', { mode: isSignUp ? 'sign_up' : 'sign_in' });
    }
  }, [isSignUp]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [touAgreed, setTouAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const { login, signup, loginWithGoogle, loading, error, clearError } = useAuthStore();

  const handleGoogleSignIn = async () => {
    setValidationError('');
    clearError();
    if (isSignUp) {
      if (!touAgreed && !privacyAgreed) {
        setValidationError(t('auth.validation_agree_both'));
        return;
      } else if (!touAgreed) {
        setValidationError(t('auth.validation_agree_tou'));
        return;
      } else if (!privacyAgreed) {
        setValidationError(t('auth.validation_agree_privacy'));
        return;
      }
    }
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      onClose();
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      // Other errors handled by store
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    clearError();

    if (!email || !password) {
      setValidationError(t('auth.validation_fill_fields'));
      return;
    }

    if (isSignUp) {
      if (!touAgreed || !privacyAgreed) {
        if (!touAgreed && !privacyAgreed) {
          setValidationError(t('auth.validation_agree_both'));
        } else if (!touAgreed) {
          setValidationError(t('auth.validation_agree_tou'));
        } else {
          setValidationError(t('auth.validation_agree_privacy'));
        }
        return;
      }
      if (password !== confirmPassword) {
        setValidationError(t('auth.validation_passwords_match'));
        return;
      }
      try {
        await signup(email, password);
        onClose();
      } catch (err) {
        // Handled by store
      }
    } else {
      try {
        await login(email, password);
        onClose();
      } catch (err) {
        // Handled by store
      }
    }
  };

  return (
    <div className="auth-modal-overlay">
      <div className="glass-panel auth-modal-content">
        <button onClick={onClose} className="auth-modal-close-btn">
          <X size={18} />
        </button>

        <div className="auth-modal-header">
          <h2 className="auth-modal-title">
            {isSignUp ? t('auth.create_account', { defaultValue: 'Create Account' }) : t('auth.welcome_back', { defaultValue: 'Welcome Back' })}
          </h2>
          <p className="auth-modal-subtitle">
            {isSignUp ? t('auth.signup_sub', { defaultValue: 'Sign up to enjoy advanced features' }) : t('auth.signin_sub', { defaultValue: 'Sign in to access your saved poses' })}
          </p>
        </div>

        {(validationError || error) && (
          <div className="auth-modal-error">
            {validationError || error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-modal-form">
          <div className="auth-modal-field">
            <label className="auth-modal-label">{t('auth.email', { defaultValue: 'Email' })}</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete={isSignUp ? "new-email" : "email"}
              className="auth-modal-input"
              required
            />
          </div>

          <div className="auth-modal-field">
            <label className="auth-modal-label">{t('auth.password', { defaultValue: 'Password' })}</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignUp ? "new-password" : "current-password"}
              className="auth-modal-input"
              required
            />
          </div>

          {isSignUp && (
            <>
              <div className="auth-modal-field">
                <label className="auth-modal-label">{t('auth.confirm_password', { defaultValue: 'Confirm Password' })}</label>
                <input 
                  type="password" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="auth-modal-input"
                  required
                />
              </div>

              <label className="splash-checkbox-wrapper splash-checkbox-consent auth-modal-checkbox-wrapper">
                <input 
                  type="checkbox" 
                  checked={touAgreed}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setTouAgreed(checked);
                    trackEvent('auth_agreement_checked', { type: 'terms_of_use', checked });
                  }}
                  className="splash-checkbox splash-checkbox-consent-input"
                />
                <span className="splash-checkbox-text auth-modal-checkbox-text">
                  {t('auth.agree_tou', { defaultValue: 'I agree to the ' })}
                  <a 
                    href="/termsofuse" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => trackGeneralEvent('tou_opened', { source: 'auth_modal' })}
                    className="splash-link-btn auth-modal-link"
                  >
                    {t('auth.terms_of_use', { defaultValue: 'Terms of Use' })}
                  </a>.
                </span>
              </label>

              <label className="splash-checkbox-wrapper splash-checkbox-consent auth-modal-checkbox-wrapper">
                <input 
                  type="checkbox" 
                  checked={privacyAgreed}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setPrivacyAgreed(checked);
                    trackEvent('auth_agreement_checked', { type: 'privacy_policy', checked });
                  }}
                  className="splash-checkbox splash-checkbox-consent-input"
                />
                <span className="splash-checkbox-text auth-modal-checkbox-text">
                  {t('auth.agree_privacy', { defaultValue: 'I have read and acknowledge the ' })}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => trackGeneralEvent('privacy_opened', { source: 'auth_modal' })}
                    className="splash-link-btn auth-modal-link"
                  >
                    {t('auth.privacy_policy', { defaultValue: 'Privacy Policy' })}
                  </a>.
                </span>
              </label>

            </>
          )}

          <button 
            type="submit" 
            disabled={loading || (isSignUp && (!touAgreed || !privacyAgreed))}
            className="auth-modal-submit-btn"
          >
            {isSignUp ? <UserPlus size={14} /> : <LogIn size={14} />}
            {loading ? t('auth.processing', { defaultValue: 'Processing...' }) : isSignUp ? t('auth.signup', { defaultValue: 'Sign Up' }) : t('auth.signin_button', { defaultValue: 'Sign In' })}
          </button>
        </form>

        <div className="auth-modal-divider">
          <div className="auth-modal-divider-line" />
          <span className="auth-modal-divider-text">{t('auth.or')}</span>
          <div className="auth-modal-divider-line" />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading || googleLoading}
          className="auth-modal-google-btn"
        >
          {googleLoading ? (
            <Loader2 size={16} className="auth-modal-google-spinner" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" className="auth-modal-google-icon">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
            </svg>
          )}
          {t('auth.continue_with_google', { defaultValue: 'Continue with Google' })}
        </button>

        <div className="auth-modal-footer">
          <span className="auth-modal-footer-text">
            {isSignUp ? t('auth.already_have_account', { defaultValue: 'Already have an account? ' }) : t('auth.dont_have_account', { defaultValue: "Don't have an account? " })}
          </span>
          <button 
            onClick={() => {
              const targetMode = !isSignUp;
              trackEvent('auth_modal_switched', { to_mode: targetMode ? 'sign_up' : 'sign_in' });
              setIsSignUp(targetMode);
              setEmail('');
              setPassword('');
              setConfirmPassword('');
              setValidationError('');
              setTouAgreed(false);
              setPrivacyAgreed(false);
              clearError();
            }}
            className="auth-modal-footer-btn"
          >
            {isSignUp ? t('auth.signin', { defaultValue: 'Sign In' }) : t('auth.signup', { defaultValue: 'Sign Up' })}
          </button>
        </div>
      </div>
    </div>
  );
};
